import type { TreatmentPlanTemplateResponse, YearPlanResponse } from '../../api/generated';
import type { ChangeSummary } from '../../hooks/useYearPlan';
import type { WeekCalculationStatuses } from '../../hooks/useWeekCalculationStatuses';
import { buildGanttGroups, computePerWeekColumnWidths } from '../../utils/ganttUtils';
import { useGanttScroll } from '../../hooks/useGanttScroll';
import ChevronIcon from '../../assets/icons/chevron.svg?react';
import { GanttWeekHeaderCorner, GanttWeekHeaderCells } from './GanttWeekHeader';
import { GanttGroupRow } from './GanttGroupRow';
import { GanttGroupLabel } from './GanttGroupLabel';
import { GanttEditToolbar } from './GanttEditToolbar';
import { useAppSelector } from '../../store/hooks';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ContextMenu } from '../ContextMenu/ContextMenu';
import styles from './GanttChart.module.css';

import type { MoveHoverParams } from './GanttCell';
import { getCurrentIsoWeek } from '../../utils/dateUtils';

interface GanttChartProps {
  yearPlan: YearPlanResponse;
  year: number;
  changeSummary?: ChangeSummary | null;
  treatmentPlanTemplates: TreatmentPlanTemplateResponse[];
  isLocked?: boolean;
  lockedByCurrentUser?: boolean;
  lockedByName?: string | null;
  onAcquireLock?: () => Promise<boolean>;
  onGroupClick?: (treatmentPlanId: string, groupId: string) => void;
  onAddTreatmentPlan?: (template: TreatmentPlanTemplateResponse) => void;
  onMoveIntervention?: (params: MoveHoverParams) => void;
  onMoveStart?: () => void;
  onMoveDiscard?: () => void;
  onMoveAccept?: () => void;
  onDiscardAll?: () => void | Promise<void>;
  onAcceptAll?: () => void | Promise<void>;
  onAddTreatmentPlanBatch?: (
    treatmentPlanTemplateId: string,
    weekNumber: number,
    mode: 'single' | 'series'
  ) => void;
  onDeleteTreatmentPlan?: (treatmentPlanId: string) => void;
  onAddPatient?: (treatmentPlanId: string, groupId: string) => void;
  onWeekStatusClick?: (weekNumber: number) => void;
  onOpenCalendar?: (weekNumber: number) => void;
  onRetryCalculationStatuses?: () => void;
  weekCalculations?: WeekCalculationStatuses;
}

export const GanttChart = ({
  yearPlan,
  year,
  changeSummary,
  treatmentPlanTemplates,
  isLocked,
  lockedByCurrentUser,
  lockedByName,
  onAcquireLock,
  onGroupClick,
  onAddTreatmentPlan,
  onMoveIntervention,
  onMoveStart,
  onMoveDiscard,
  onMoveAccept,
  onDiscardAll,
  onAcceptAll,
  onAddTreatmentPlanBatch,
  onDeleteTreatmentPlan,
  onAddPatient,
  onWeekStatusClick,
  onOpenCalendar,
  onRetryCalculationStatuses,
  weekCalculations
}: GanttChartProps) => {
  const { t } = useTranslation();

  const isEditMode = useAppSelector((state) => state.applicationSlice.isEditMode);
  const totalWeeks = yearPlan.weeks.length;
  const groups = useMemo(() => buildGanttGroups(yearPlan), [yearPlan]);
  const weekWidths = useMemo(
    () => computePerWeekColumnWidths(groups, totalWeeks),
    [groups, totalWeeks]
  );
  const weekGridTemplateColumns = weekWidths.map((w) => `${String(w)}px`).join(' ');
  const {
    containerRef,
    scrollLeft,
    scrollRight,
    scrollToWeek,
    scrollToStart,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp
  } = useGanttScroll();

  const lastScrolledYearRef = useRef<number | null>(null);

  useEffect(() => {
    if (lastScrolledYearRef.current === year) return;
    lastScrolledYearRef.current = year;

    if (year === new Date().getFullYear()) {
      scrollToWeek(getCurrentIsoWeek() - 1, weekWidths);
    } else {
      scrollToStart();
    }
  }, [year, weekWidths, scrollToWeek, scrollToStart]);

  const availableTemplates = useMemo(() => {
    const usedTemplateIds = new Set(
      yearPlan.treatmentPlans.map((tp) => tp.treatmentPlanTemplateId)
    );
    return treatmentPlanTemplates.filter((t) => t.id && !usedTemplateIds.has(t.id));
  }, [yearPlan.treatmentPlans, treatmentPlanTemplates]);

  const [menuAnchorRect, setMenuAnchorRect] = useState<DOMRect | null>(null);

  const handleAddClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorRect(e.currentTarget.getBoundingClientRect());
  }, []);

  const menuItems = useMemo(
    () =>
      availableTemplates.map((tp) => ({
        label: tp.name,
        onClick: () => {
          onAddTreatmentPlan?.(tp);
          setMenuAnchorRect(null);
        }
      })),
    [availableTemplates, onAddTreatmentPlan]
  );

  return (
    <div className={`${styles.wrapper} ${isEditMode ? styles.wrapperEditMode : ''}`}>
      <div className={styles.toolbar}>
        <GanttEditToolbar
          isLocked={isLocked}
          lockedByCurrentUser={lockedByCurrentUser}
          lockedByName={lockedByName}
          changeSummary={changeSummary}
          onAcquireLock={onAcquireLock}
          onDiscardAll={onDiscardAll}
          onAcceptAll={onAcceptAll}
        />
        <div className={styles.scrollButtons}>
          <button
            type="button"
            className={styles.scrollButton}
            onClick={scrollLeft}
            aria-label="Scroll left"
          >
            <ChevronIcon className={styles.chevronLeft} />
          </button>
          <button
            type="button"
            className={styles.scrollButton}
            onClick={scrollRight}
            aria-label="Scroll right"
          >
            <ChevronIcon className={styles.chevronRight} />
          </button>
        </div>
      </div>
      <div className={styles.container} tabIndex={0}>
        <div className={styles.ganttLayout}>
          {/* The corner has no button of its own, so it needs telling that the
              cells grew by a row — the two must stay one visual band. */}
          <GanttWeekHeaderCorner hasCalendarRow={onOpenCalendar !== undefined} />
          {groups.map((group) => (
            <GanttGroupLabel key={group.templateId} group={group} isEditMode={isEditMode} />
          ))}
          <div
            ref={containerRef}
            className={styles.scrollColumn}
            style={{
              gridTemplateColumns: weekGridTemplateColumns,
              gridRow: `1 / span ${groups.length + 2}`
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <GanttWeekHeaderCells
              yearPlan={yearPlan}
              onWeekStatusClick={onWeekStatusClick}
              onOpenCalendar={onOpenCalendar}
              onRetryCalculationStatuses={onRetryCalculationStatuses}
              weekCalculations={weekCalculations}
            />
            {groups.map((group) => (
              <GanttGroupRow
                key={group.templateId}
                group={group}
                totalWeeks={totalWeeks}
                weeks={yearPlan.weeks}
                isEditMode={isEditMode}
                onGroupClick={onGroupClick}
                onMoveIntervention={onMoveIntervention}
                onMoveStart={onMoveStart}
                onMoveDiscard={onMoveDiscard}
                onMoveAccept={onMoveAccept}
                onAddTreatmentPlanBatch={onAddTreatmentPlanBatch}
                onDeleteTreatmentPlan={onDeleteTreatmentPlan}
                onAddPatient={onAddPatient}
              />
            ))}
          </div>
          <div className={styles.bottomSpacer} />
        </div>
      </div>
      <button
        type="button"
        className={`${styles.addTreatmentTypeButton} ${isEditMode ? styles.addTreatmentTypeButtonVisible : ''}`}
        onClick={handleAddClick}
        tabIndex={isEditMode ? 0 : -1}
        disabled={availableTemplates.length === 0}
        data-tooltip={availableTemplates.length === 0 ? t('all-treatment-types-added') : undefined}
      >
        {t('add-treatment-type')}
      </button>
      {menuAnchorRect && (
        <ContextMenu
          anchorRect={menuAnchorRect}
          items={menuItems}
          onClose={() => setMenuAnchorRect(null)}
        />
      )}
    </div>
  );
};
